import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { cn } from '../utils/cn';
import './SectionsCard.css';

// Columns blur in one after another instead of all popping in at once.
const GRID_VARIANTS = {
  hidden: { transition: { staggerChildren: 0.05, staggerDirection: -1 } },
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.03 } },
};

const COLUMN_VARIANTS = {
  hidden: { opacity: 0, filter: 'blur(6px)' },
  visible: { opacity: 1, filter: 'blur(0px)' },
};

const COLUMN_TRANSITION = { duration: 0.28, ease: 'easeOut' };

const STORAGE_PREFIX = 'sectionscard:open:';

// persistId is a hardcoded, hand-written random-looking string per usage site (e.g. generated
// once with `openssl rand -hex 5` and pasted into the section definition) — NOT generated at
// runtime, or every render would get a fresh key and nothing would ever persist. Its only job is
// to keep this section's stored open/closed state from colliding with any other section's, so two
// unrelated collapsible sections never fight over the same localStorage key.
function readPersistedOpen(persistId, fallback) {
  if (!persistId || typeof window === 'undefined') return fallback;
  try {
    const raw = window.localStorage.getItem(`${STORAGE_PREFIX}${persistId}`);
    return raw === null ? fallback : raw === '1';
  } catch {
    return fallback;
  }
}

function writePersistedOpen(persistId, isOpen) {
  if (!persistId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${persistId}`, isOpen ? '1' : '0');
  } catch {
    // localStorage unavailable (private mode, quota) — persistence just silently no-ops
  }
}

/**
 * Generic card shell for grouping filter/config sections: a header bar per
 * section followed by a grid of columns that divides into vertical dividers
 * on desktop and stacks (horizontal dividers) on mobile.
 *
 * This component only owns the wrapper mechanics (card, header bar, grid,
 * dividers, optional collapse toggle, optional header action slot). What
 * goes inside each column is entirely up to the caller.
 *
 * Adapted from the reporting UI's Tailwind original — same API, styled with
 * plain CSS against the tokens in tokens.css (see SectionsCard.css).
 *
 * @param {Array<{key?: string, title: string, columns: React.ReactNode[], columnWidths?: string, collapsible?: boolean, defaultOpen?: boolean, persistId?: string, headerAction?: React.ReactNode, onToggle?: (isOpen: boolean) => void}>} sections
 *   persistId, when set on a collapsible section, remembers open/closed in localStorage across
 *   reloads. Must be a hardcoded string unique to that usage site — see readPersistedOpen above.
 * @param {'default'|'tinted'|'rail'|'segmented'} variant
 */
export default function SectionsCard({ sections = [], variant = 'default', className }) {
  if (variant === 'segmented') {
    return (
      <div className={cn('sc-card', 'sc-card--segmented', className)}>
        {sections.map(({ key, ...section }) => (
          <div key={key ?? section.title} className="sc-panel">
            <Section {...section} variant="default" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('sc-card', className)}>
      {sections.map(({ key, ...section }) => (
        <Section key={key ?? section.title} {...section} variant={variant} />
      ))}
    </div>
  );
}

function Section({
  title,
  columns = [],
  columnWidths,
  variant,
  collapsible = false,
  defaultOpen = true,
  persistId,
  headerAction,
  onToggle,
  isOpen: controlledIsOpen,
}) {
  const isControlled = controlledIsOpen !== undefined;
  const [internalIsOpen, setInternalIsOpen] = useState(() => readPersistedOpen(persistId, defaultOpen));
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  const toggle = () => {
    const next = !isOpen;
    if (!isControlled) {
      writePersistedOpen(persistId, next);
      setInternalIsOpen(next);
    }
    onToggle?.(next);
  };

  if (variant === 'rail') {
    return (
      <div className="sc-rail">
        <div className="sc-rail-label">
          <span>{title}</span>
        </div>
        <SectionGrid columns={columns} columnWidths={columnWidths} />
      </div>
    );
  }

  return (
    <div>
      {title && (
        <div
          className={cn(
            'sc-header',
            isOpen && 'sc-header--open',
            variant === 'tinted' && 'sc-header--tinted',
            // The whole header row reads as the control when the section collapses — clicking the
            // title (not just a small glyph) is what people actually try first.
            collapsible && 'sc-header--collapsible'
          )}
        >
          {collapsible ? (
            <button
              type="button"
              onClick={toggle}
              aria-expanded={isOpen}
              aria-label={isOpen ? `Collapse ${title}` : `Expand ${title}`}
              className="sc-header-button"
            >
              <span className="sc-chevron">
                <ChevronDown className={cn('sc-chevron-icon', !isOpen && 'sc-chevron-icon--closed')} />
              </span>
              <span className="sc-title">{title}</span>
              <span className="sc-toggle-label">{isOpen ? 'Hide' : 'Show'}</span>
            </button>
          ) : (
            <div className="sc-header-static">
              <span className="sc-title">{title}</span>
            </div>
          )}
          {headerAction ? <div className="sc-header-action">{headerAction}</div> : null}
        </div>
      )}
      <AnimatePresence initial={false}>
        {isOpen && <SectionGrid columns={columns} columnWidths={columnWidths} />}
      </AnimatePresence>
    </div>
  );
}

function SectionGrid({ columns, columnWidths }) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.div
      className="sc-grid"
      style={
        columnWidths
          ? { '--sections-card-template': columnWidths }
          : { '--sections-card-cols': columns.length }
      }
      variants={GRID_VARIANTS}
      initial="hidden"
      animate="visible"
      exit="hidden"
    >
      {columns.map((column, index) => (
        <motion.div
          key={index}
          className="sc-column"
          variants={reduceMotion ? undefined : COLUMN_VARIANTS}
          transition={COLUMN_TRANSITION}
        >
          {column}
        </motion.div>
      ))}
    </motion.div>
  );
}
